/**
 * React Query hooks for Company-owned arches and arche audience (ADR-026).
 * Server state only; every mutation invalidates the queries it changes.
 *
 * The server decides everything shown here — who owns an arche, who may
 * govern it, what an acceptance discloses. These hooks only fetch and send.
 *
 * What a change makes stale:
 *   - an OWNERSHIP move (accept, release, withdraw, offer) changes who is paid
 *     and who pays the arche's costs — every contributor-arche section
 *     (`contributorArches.all`: ownership, billing, the dashboard list), the
 *     account menu's proposals (`archeGovernance.all`) and the Company's list;
 *   - an AUDIENCE change (restrict, open up, grant, revoke) changes that arche's
 *     page (`contributorArches.detail`), what is shared with the viewer, and the
 *     owning Company's list (it badges each arche's audience).
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  api,
  type ArcheAudienceDto,
  type ArcheAudienceKind,
  type ArchesSharedWithMeCursor,
  type CompanyArcheCollaboratorRole,
  type CompanyArcheTransferExpectedTerms,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

function invalidateOwnershipViews(qc: QueryClient, companyId?: string): void {
  void qc.invalidateQueries({ queryKey: queryKeys.contributorArches.all });
  void qc.invalidateQueries({ queryKey: queryKeys.archeGovernance.all });
  if (companyId) void qc.invalidateQueries({ queryKey: queryKeys.companies.arches(companyId) });
}

// ── Company side ─────────────────────────────────────────────────────────────

export function useCompanyArches(companyId: string | null) {
  return useQuery({
    queryKey: queryKeys.companies.arches(companyId ?? 'none'),
    enabled: companyId !== null,
    queryFn: () => api.listCompanyArches(companyId as string),
  });
}

export function useCompanyArcheTransferDisclosure(companyId: string, transferId: string | null) {
  return useQuery({
    queryKey: queryKeys.companies.archeTransferDisclosure(companyId, transferId ?? 'none'),
    enabled: transferId !== null,
    queryFn: () => api.getCompanyArcheTransferDisclosure(companyId, transferId as string),
    // A disclosure is shown once and accepted against; never serve it stale.
    staleTime: 0,
  });
}

function useInvalidateCompanyArches(companyId: string) {
  const qc = useQueryClient();
  return () => invalidateOwnershipViews(qc, companyId);
}

export function useDecideCompanyArcheTransfer(companyId: string) {
  const invalidate = useInvalidateCompanyArches(companyId);
  return useMutation({
    mutationFn: (input: {
      transferId: string;
      decision: 'accept' | 'decline';
      expected?: CompanyArcheTransferExpectedTerms;
    }) =>
      api.decideCompanyArcheTransfer(companyId, input.transferId, {
        decision: input.decision,
        expected: input.expected,
      }),
    onSuccess: invalidate,
  });
}

export function useReleaseCompanyArche(companyId: string) {
  const invalidate = useInvalidateCompanyArches(companyId);
  return useMutation({
    mutationFn: (input: { archeId: string; username: string; reason?: string }) =>
      api.releaseCompanyArche(companyId, input.archeId, {
        username: input.username,
        reason: input.reason,
      }),
    onSuccess: invalidate,
  });
}

export function useCancelCompanyArcheRelease(companyId: string) {
  const invalidate = useInvalidateCompanyArches(companyId);
  return useMutation({
    mutationFn: (transferId: string) => api.cancelCompanyArcheRelease(companyId, transferId),
    onSuccess: invalidate,
  });
}

// ── The arche's own side (creator, release recipient) ────────────────────────

// Ownership and audience keep the app's default retry (a 4xx — "not yours to
// see" — is answered at once; a 5xx gets one retry before the panel shows its
// error with a Retry of its own).

export function useArcheOwnership(archeId: string) {
  return useQuery({
    queryKey: queryKeys.contributorArches.ownership(archeId),
    queryFn: () => api.getArcheOwnership(archeId),
  });
}

export function useArcheTransferCompanies(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.archeGovernance.transferCompanies(),
    enabled: options?.enabled ?? true,
    queryFn: () => api.listArcheTransferCompanies(),
  });
}

/**
 * Everything the account menu answers for: my own offers and releases
 * (`transfers`) and, as a Company owner, offers waiting for my Companies
 * (`offersToMyCompanies`). One request for both.
 */
export function useMyArcheTransfers(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.archeGovernance.myTransfers(),
    enabled: options?.enabled ?? true,
    queryFn: () => api.listMyArcheTransfers(),
  });
}

/** Company → Owned arches: revoke a collaborator from one of the Company's arches (offboarding). */
export function useRevokeCompanyArcheCollaborator(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { archeId: string; userId: string }) =>
      api.revokeArcheCollaborator(input.archeId, input.userId),
    onSuccess: (_result, input) => {
      void qc.invalidateQueries({ queryKey: queryKeys.companies.arches(companyId) });
      void qc.invalidateQueries({ queryKey: queryKeys.contributorArches.detail(input.archeId) });
    },
  });
}

/**
 * Company → Owned arches: add a publisher or viewer to one of the Company's
 * arches by email or username. The CLI cannot do this for a company-owned
 * arche (it authenticates with an API key, which Company governance refuses).
 */
export function useAddCompanyArcheCollaborator(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      archeId: string;
      identifier: { kind: 'email' | 'username'; value: string };
      role: CompanyArcheCollaboratorRole;
    }) =>
      api.addArcheCollaborator(input.archeId, {
        identifier: input.identifier,
        role: input.role,
      }),
    onSuccess: (_result, input) => {
      void qc.invalidateQueries({ queryKey: queryKeys.companies.arches(companyId) });
      void qc.invalidateQueries({ queryKey: queryKeys.contributorArches.detail(input.archeId) });
    },
  });
}

/**
 * Company → Owned arches: change a team member's role (publisher ↔ viewer).
 * Role only — the server keeps whatever expiry the member has.
 */
export function useChangeCompanyArcheCollaboratorRole(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { archeId: string; userId: string; role: CompanyArcheCollaboratorRole }) =>
      api.updateArcheCollaboratorRole(input.archeId, input.userId, { role: input.role }),
    onSuccess: (_result, input) => {
      void qc.invalidateQueries({ queryKey: queryKeys.companies.arches(companyId) });
      void qc.invalidateQueries({ queryKey: queryKeys.contributorArches.detail(input.archeId) });
    },
  });
}

function useInvalidateArcheGovernance() {
  const qc = useQueryClient();
  return () => invalidateOwnershipViews(qc);
}

export function useProposeArcheTransfer(archeId: string) {
  const invalidate = useInvalidateArcheGovernance();
  return useMutation({
    mutationFn: (body: { companyId: string; reason?: string }) =>
      api.proposeArcheTransferToCompany(archeId, body),
    onSuccess: invalidate,
  });
}

export function useCancelArcheTransfer() {
  const invalidate = useInvalidateArcheGovernance();
  return useMutation({
    mutationFn: (transferId: string) => api.cancelArcheTransfer(transferId),
    onSuccess: invalidate,
  });
}

export function useDecideArcheRelease() {
  const invalidate = useInvalidateArcheGovernance();
  return useMutation({
    mutationFn: (input: { transferId: string; decision: 'accept' | 'decline' }) =>
      api.decideArcheRelease(input.transferId, input.decision),
    onSuccess: invalidate,
  });
}

// ── Audience ─────────────────────────────────────────────────────────────────

export function useArcheAudience(archeId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.contributorArches.audience(archeId),
    enabled: options?.enabled ?? true,
    queryFn: () => api.getArcheAudience(archeId),
  });
}

function useInvalidateAudience(archeId: string) {
  const qc = useQueryClient();
  return () => {
    // The owning Company (if any) is read from the audience already on screen,
    // before the refetch below replaces it.
    const owningCompanyId = qc.getQueryData<ArcheAudienceDto>(
      queryKeys.contributorArches.audience(archeId),
    )?.owningCompany?.companyId;
    void qc.invalidateQueries({ queryKey: queryKeys.contributorArches.detail(archeId) });
    void qc.invalidateQueries({ queryKey: queryKeys.archeGovernance.sharedWithMe() });
    if (owningCompanyId) {
      void qc.invalidateQueries({ queryKey: queryKeys.companies.arches(owningCompanyId) });
    }
  };
}

export function useSetArcheAudience(archeId: string) {
  const invalidate = useInvalidateAudience(archeId);
  return useMutation({
    mutationFn: (input: { audience: ArcheAudienceKind; grantOwningCompany?: boolean }) =>
      input.grantOwningCompany
        ? api.setArcheAudience(archeId, input.audience, { grantOwningCompany: true })
        : api.setArcheAudience(archeId, input.audience),
    onSuccess: invalidate,
  });
}

export function useFinishArcheRestriction(archeId: string) {
  const invalidate = useInvalidateAudience(archeId);
  return useMutation({
    mutationFn: () => api.finishArcheRestriction(archeId),
    onSuccess: invalidate,
  });
}

export function useGrantArcheAudience(archeId: string) {
  const invalidate = useInvalidateAudience(archeId);
  return useMutation({
    mutationFn: (
      body:
        | { granteeType: 'user'; username: string }
        | { granteeType: 'workspace' | 'company' | 'guild'; granteeId: string },
    ) => api.grantArcheAudience(archeId, body),
    onSuccess: invalidate,
  });
}

export function useRevokeArcheAudienceGrant(archeId: string) {
  const invalidate = useInvalidateAudience(archeId);
  return useMutation({
    mutationFn: (grantId: string) => api.revokeArcheAudienceGrant(archeId, grantId),
    onSuccess: invalidate,
  });
}

/** Restricted arches shared with me, a page at a time. */
export function useArchesSharedWithMe(options?: { enabled?: boolean }) {
  const query = useInfiniteQuery({
    queryKey: queryKeys.archeGovernance.sharedWithMe(),
    enabled: options?.enabled ?? true,
    queryFn: ({ pageParam }) => api.listArchesSharedWithMe(pageParam),
    initialPageParam: null as ArchesSharedWithMeCursor | null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  return {
    arches: query.data?.pages.flatMap((p) => p.arches) ?? [],
    loading: query.isLoading,
    error:
      query.error instanceof Error
        ? query.error.message
        : query.error
          ? 'Failed to load arches shared with you'
          : null,
    refresh: () => void query.refetch(),
    hasMore: query.hasNextPage,
    isFetchingMore: query.isFetchingNextPage,
    loadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
    },
  };
}
